import * as React from 'react';
import { Provider, ProviderContext } from '../../shared-exports';
import { Header, Text, colors, radius, fontSizes } from '../../style';
import { Modal, Button, Row, Col, Input, Icon, Alert, Radio } from '../../components/ui';
import { InputProps } from '../../components/ui';
import { IconProps } from '../../components/ui';

interface Interface {
	/** Shows the modal and returns a disposer function */
	show: (options: EditorModalOptions) => Promise<() => void>
	/** Confirms and closes the modal */
	confirm: () => any
	/** Cancels and closes the modal */
	cancel: () => any
	/** Cancels and closes the modal */
	hide: () => any
	/** Remotly calls onOK */
	callOnOk: () => Promise<void>
}

/** Modal display configuration */
interface EditorModalOptions {
  /** Text to show in the confirm button, set to undefined or empty string to not show an ok button, defaults to "Ok" */
  okText?: string;
  /** Text to show in the cancel button, set to undefined or empty string to not show an ok button, defaults to "Avbryt" */
  cancelText?: string;
  /** Event to call when to validate the modal input.
   * if it returns a string, "onOk" is not called and its is displayed as an error
   */
  onValidate?: () => Promise<string | React.ReactNode | undefined>;
  /** Event to call when the user confirms the modal */
  onOk?: () => any;
  /** Event to call when the user cancels the modal */
  onCancel?: () => any;
  /** Makes the modal impossible to close */
  unclosable?: boolean;
  /** The color type of the modal, defaults to primary */
  type?: "primary" | "warning" | "error" | "info";
  /** If set to true the modal will fill the entire screen */
  filling?: boolean;
  /** The icon of the modal, if any */
  icon?: IconProps["type"];
  /** The title of the modal, if any */
  title?: React.ReactNode;
  /** The body contentof the modal, if any */
  body?: React.ReactNode;
  /** The footer content of the modal, if any */
  footer?: React.ReactNode;
  /** A input to display in the modal */
	input?: InputOptions;
	/** A list of the different options for a group of radio buttons */
  radioOptions?: Array<InputOptions>;
  /** A list of inputs to display in the modal */
  inputs?: Array<InputOptions>;
  step?: React.ReactNode;
  className?: string;
  modalPadding?: boolean;
  description?: React.ReactNode;
  wrapClassName?: string;
  centered?: boolean;
}

/** A configurable input to show in the modal */
interface InputOptions {
	/** The type of input to display */
	type: "number" | "text" | "textarea" | "password" | "button" | "radio" | "radioButton",
	pattern: string
	/** Optional text label to render above the input */
	label?: string
	/** Optional flag to trigger automatic text selection of the inputs value */
	preselected?: boolean
	/** The placeholder value */
	placeholder?: InputProps['placeholder'],
	/** The default value to start with */
	defaultValue?: InputProps['defaultValue']
	/** Default checked */
	defaultChecked?: boolean
	/** Event to call when the input value changes */
	onChange?: (value: string) => any
	/** The number of row to render, only aplies to the textarea type, defaults to 7 */
	/** Event to call when the input value changes */
	onClick?: (value: string) => any
	radioOptions?: Array<InputOptions>;
	rows?: number
	/** Datalist to use */
	list?: string
	/** Display value for button inputs */
	value?: string
}

const DefaultContext: Interface = {
	show: null!,
	confirm: null!,
	cancel: null!,
	hide: null!,
	callOnOk: null!
}

/** The context that will be used by the provider */
export const ProductInfoModalContext: React.Context<Interface> = React.createContext(DefaultContext);
export interface ProductInfoModalContext extends ProviderContext<Interface> { };

interface Props { }
interface State {
	/** Any validation errors that has occured */
	errors: string | React.ReactNode | undefined
	/** The current modal configuration */
	options: EditorModalOptions
	/** Indicates that a modal is being displayed */
	opened?: boolean
}

/** Provider for showing predefined and custom user-input modals  */
export class ProductInfoModalProvider extends Provider<Interface, Props, State> {

	/** Exposes the context to use */
	protected use() { return ProductInfoModalContext }

	/** Declares the initial state */
	protected initialState(): Interface & State {
		return {
			...DefaultContext,
			errors: undefined,
			show: this.onShow.bind(this),
			cancel: this.onCancel.bind(this),
			confirm: this.onOk.bind(this),
			hide: this.onHide.bind(this),
			opened: false,
			options: {
				type: "primary",
				okText: "Ok",
				cancelText: "Avbryt",
				title: undefined,
				icon: undefined,
				body: undefined,
				input: undefined,
				inputs: undefined,
				onOk: undefined,
				onValidate: undefined,
				onCancel: undefined,
				step: undefined,
				className: "",
				modalPadding: true
			},
			callOnOk: this.onOk.bind(this)
		}
	}

	/** Updates the state of the provider, will reset the state before called so no previous configuration is rendered */
	protected async updateState(state: Partial<Interface> | Interface) {
		// Close the modal if opened and reset the state
		await this.setState(this.initialState());

		// Set the new options and open the modal
		await this.setState(state as Interface & State);
	}

	/** Will display the modal with the given configuration when called */
	protected async onShow(options: EditorModalOptions) {

		// Set the new options and open the modal
		await this.updateState({ options, opened: true } as Interface & State);
		return () => {this.updateState({ opened: false }  as Interface & State)};
	}

	/** 
	 * Closes the modal and perform any configured action when the user confirm.
	 * If onValidate is passed it needs to pass validation before closing
	 */
	private async onOk() {
		const { onOk, onValidate } = this.state.options;

		const error = onValidate ? await onValidate() : undefined;
		
		if (error) {
			await this.setState({ errors: error });
		}
		else {
			await this.setState({ opened: false });
			onOk && onOk();
		}
	}

	/** Closes the modal and perform any configured action when the user cancels */
	private async onCancel() {
		const { onCancel } = this.state.options;
		await this.setState({ opened: false });
		onCancel && onCancel();
	}

	/** Closes the modal without performing any configured action */
	private async onHide() {
		await this.setState({ opened: false });
	}

	/** Renders a input from a given input options as declered in the context interface */
	private renderInput(input: InputOptions, index: number, listLength: number) {
		const inputSize = listLength <= 2 ? "large" : "default";
		const isLastInput = (index == listLength - 1);

		if (input.type == "textarea"){
			return (
				<React.Fragment key={index}>
					{input.label && <Text className="text-black font-semibold">{input.label}</Text>}
					{<Input.TextArea
							id={`modalInput${index}`}
							rows={input.rows || 4}
							autoFocus={(index == 0)}
							onFocus={input.preselected ? (e) => e.target.select() : undefined}
							placeholder={input.placeholder}
							defaultValue={input.defaultValue}
							onChange={event => input.onChange ? input.onChange(event.target.value) : null}
							style={{
								padding: "0.8em",
								marginTop: inputSize == "large" ? "1.2em" : undefined,
								width: "100%",
								borderColor: colors.faded,
								borderRadius: radius.medium
							}}
						/>}
				</React.Fragment>
			)
		}

		if (input.type == "button"){
			const button: React.CSSProperties = {
        borderRadius: radius.normal,
        margin: "3px",
        width: "23%",
        fontSize: fontSizes.xs,
        textAlign: "center",
				padding: "0px 5px",
      };
			return (
        <React.Fragment key={index}>
          {
            <Button
              id={`modalInput${index}`}
              //onFocus={input.preselected ? e => e.target.select() : undefined}
              //placeholder={input.placeholder}
              //defaultValue={input.defaultValue}
              onClick={event =>
                input.onClick ? input.onClick((event.target as HTMLButtonElement).value) : null
              }
							style={button}
            >
              {input.value}
            </Button>
          }
        </React.Fragment>
      );
		}

		if(input.type === "radio"){
			const radiobutton: React.CSSProperties = {
				borderRadius: radius.normal,
				margin: "3px",
				width: "23%",
				fontSize: fontSizes.xs,
				textAlign: "center",
				padding: "0px 5px"
			};

			return (
				<React.Fragment key={index}>
				{
					<Radio.Group
					defaultValue={input.defaultValue as string | number | undefined}
					size="large"
					buttonStyle="outline"
					onChange={event => {
						input.onChange ? input.onChange(String(event.target.value)) : null;
									}}
								style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap' }}	
					>
					{input.radioOptions && input.radioOptions.map((radioOption: any, index) =>{
										return (
						<Radio.Button
							value={radioOption.value}
												defaultChecked={radioOption.defaultChecked}
							key={`${radioOption.type}-${index}`}
							style={radiobutton}
						>
												<div onClick={() => radioOption.onClick}>{radioOption.label}</div>
						</Radio.Button>
						);
									}
						//this.renderInput(radioOption, index, input.radioOptions.length)
					)}
					</Radio.Group>
				} 
				</React.Fragment>
			);
		}

		return (
			<React.Fragment key={index}>
				{input.label && <Text className="text-black font-semibold">{input.label}</Text>}
				<Input
						list={input.list}
						id={`modalInput${index}`}
						size={inputSize}
						type={input.type}
						pattern={input.pattern}
						autoFocus={(index == 0)}
						onFocus={input.preselected ? (e) => e.target.select() : undefined}
						placeholder={input.placeholder}
						defaultValue={input.defaultValue}
						onChange={event => { input.onChange ? input.onChange(event.target.value) : null }}
						onPressEnter={() => {
							const nextInput = document.getElementById(`modalInput${index + 1}`);
							if (!isLastInput && nextInput) {
								nextInput.focus();
								return;
							}
							return this.onOk();
						}}
						style={{
							marginBottom: "4px",
							marginTop: "3px"
						}}
					/>
			</React.Fragment>
		)
		/*
		return (
			<React.Fragment key={index}>
				{input.label && <Text className="text-black font-semibold">{input.label}</Text>}
				{input.type == "textarea"
					? <Input.TextArea
						id={`modalInput${index}`}
						rows={input.rows || 4}
						autoFocus={(index == 0)}
						onFocus={input.preselected ? (e) => e.target.select() : undefined}
						placeholder={input.placeholder}
						defaultValue={input.defaultValue}
						onChange={event => input.onChange ? input.onChange(event.target.value) : null }
						style={{
							padding: "0.8em",
							marginTop: inputSize == "large" ? "1.2em" : undefined,
							width: "100%",
							borderColor: colors.faded,
							borderRadius: radius.medium
						}}
					/>
					: <Input
						list={input.list}
						id={`modalInput${index}`}
						size={inputSize}
						type={input.type}
						pattern={input.pattern}
						autoFocus={(index == 0)}
						onFocus={input.preselected ? (e) => e.target.select() : undefined}
						placeholder={input.placeholder}
						defaultValue={input.defaultValue}
						onChange={event => { input.onChange ? input.onChange(event.target.value) : null }}
						onPressEnter={() => {
							const nextInput = document.getElementById(`modalInput${index + 1}`);
							if (!isLastInput && nextInput) {
								nextInput.focus();
								return;
							}
							return this.onOk();
						}}
						style={{
							marginBottom: "10px",
							marginTop: "3px"
						}}
					/>}
			</React.Fragment>
		);
		*/
	}

	/** Renders the modal with the current configuration when opened */
	private renderModal() {
		let { step, filling, type, icon, title, body, footer, input, inputs, okText, cancelText, unclosable, className, modalPadding, description, wrapClassName, centered = false } = this.state.options;
		const { errors } = this.state;
		const inputList = input ? [input, ...(inputs || [])] : (inputs || []);
		let hasInputs = inputList.length;
		let hasFooter = (cancelText && cancelText.length) || (okText && okText.length) || footer || errors;

		return (
      <Modal
        wrapClassName={wrapClassName}
        visible={this.state.opened}
        closable={!unclosable}
        keyboard={false}
        maskClosable={!unclosable}
        style={{
          top:
            (filling || hasInputs) && false
              ? __PROPS__.deviceHasNotch
                ? "52px"
                : "27px"
              : undefined,
          bottom: (filling || hasInputs) && false ? 0 : undefined
        }}
        bodyStyle={{ padding: 0 }}
        centered={centered}
        destroyOnClose={true}
        onOk={this.onOk.bind(this)}
        onCancel={this.onCancel.bind(this)}
        footer={null}
        className={className}
      >
        <Row
          style={{
            // height: filling ? viewHeight(70) : undefined,
            // maxHeight: filling ? viewHeight(70) : (hasInputs ? viewHeight(40) : undefined),
            overflowY: filling || hasInputs ? "scroll" : "hidden",
            overflowX: "hidden",
            padding: modalPadding || modalPadding === undefined ? "20px" : 0,
            paddingBottom: hasInputs ? 0 : "20px"
          }}
        >
          {step ? step : undefined}
          <Col span={24} style={{ display: "flex", alignItems: "center" }}>
            {icon && (
              <Icon
                type={icon}
                style={{
                  padding: "0.2em",
                  fontSize: 'var(--icon-xl)',
                  color: colors[type || "primary"]
                }}
              />
            )}
            {icon && title && (
              <Header
                style={{
                  textAlign: "left",
                  marginLeft: "10px",
                  paddingTop: "20px",
                  paddingBottom: "20px"
                }}
              >
                <span className="font-semibold text-xl">
                  {title}
                </span>
              </Header>
            )}
            {!icon && title && (
              <Header
                style={{ textAlign: "left" }}
              >
                <span className="font-semibold text-xl">
                  {title}
                </span>
              </Header>
            )}
          </Col>
          <Col span={24}>
            {body && <div className="text-[1.05em]">{body}</div>}
            {hasInputs ? (
              inputList.some(inputOptions => inputOptions.type === "button") ? (
                <div className="flex flex-row items-center justify-center pb-2.5">
                  {inputList.map((inputOptions, index) =>
                    this.renderInput(inputOptions, index, inputList.length)
                  )}
                </div>
              ) : (
                inputList.map((inputOptions, index) =>
                  this.renderInput(inputOptions, index, inputList.length)
                )
              )
            ) : null}
            {description ? (
              <div className="text-[1.1em] text-black mt-1.5">
                {" "}
                {description}{" "}
              </div>
            ) : null}
          </Col>
        </Row>
        {hasFooter && (
          <Row className="modal-footer">
            {errors && errors !== "userAccess" && (
              <div className="text-[1.1em] pt-[1.2em] pb-[1.3em]">
                <Alert showIcon message={errors} type="error" />
              </div>
            )}
            {footer && (
              <div className="text-[1.1em] pt-[1.2em] pb-[1.3em]">
                {footer}
              </div>
            )}
            {cancelText && cancelText.length ? (
              <Button
                size="large"
                key="cancel"
                onClick={this.onCancel.bind(this)}
              >
                {cancelText}
              </Button>
            ) : null}
            {okText && okText.length && okText !== "klar" ? (
              <Button
                size="large"
                key="submit"
                type="primary"
                loading={false}
                onClick={this.onOk.bind(this)}
              >
                {okText}
              </Button>
            ) : null}
            {okText && okText === "klar" ? (
              <Button
                size="large"
                onClick={this.onOk.bind(this)}
                loading={false}
                type="primary"
                block
              >
                Klar
              </Button>
            ) : null}
          </Row>
        )}
      </Modal>
    );
	}

	/** Overridden React life cycle method for enabling the rendering of the modal */
	public render() {
		const Context = this.use();
		return (
			<Context.Provider value={this.state}>
				{this.props.children}
				{this.renderModal()}
			</Context.Provider>
		)
	}
}
